import React, { useCallback, useEffect, useState } from 'react';
import { View, Text, StyleSheet, ActivityIndicator, Alert, ScrollView, Modal } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import '../../i18n';
import { useAuth } from '../../features/auth/useAuth';
import { useDb } from '../../db/client';
import {
  getProject,
  updateProject,
  deleteProject,
  listProjectSupplies,
  setProjectPracticedPrice,
  type ProjectSupplyView,
} from '../../features/projects/projectRepository';
import { listSupplies } from '../../features/supplies/supplyRepository';
import { listProjectPhotos } from '../../features/projects/projectPhotos';
import { ProjectSuppliesSection } from './ProjectSuppliesSection';
import { ProjectPhotosSection } from './ProjectPhotosSection';
import { parseLocaleNumber } from '../shared/parseLocaleNumber';
import { formatLocaleNumber } from '../shared/formatLocaleNumber';
import type { Project, Supply, ProjectPhoto } from '../../db/schema';
import { colors, fonts, radii, spacing } from '../theme';
import { TextField } from '../shared/TextField';
import { Button } from '../shared/Button';
import { Chip } from '../shared/Chip';

const STATUS_OPTIONS: Project['status'][] = ['not_started', 'in_progress', 'finished', 'cancelled'];

export function ProjectDetailScreen() {
  const { t } = useTranslation();
  const router = useRouter();
  const { id: rawId } = useLocalSearchParams<{ id: string }>();
  const id = Array.isArray(rawId) ? rawId[0] : rawId;
  const { user } = useAuth();
  const { db, ready } = useDb();

  const [project, setProject] = useState<Project | null>(null);
  const [links, setLinks] = useState<ProjectSupplyView[]>([]);
  const [catalog, setCatalog] = useState<Supply[]>([]);
  const [photos, setPhotos] = useState<ProjectPhoto[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [recipeUrl, setRecipeUrl] = useState('');
  const [saving, setSaving] = useState(false);

  // Diálogo de "preço praticado" (Etapa 13 — spec S04: "ao marcar o projeto
  // como finalizado, oferecer registrar o preço praticado"). Modal + TextInput
  // em vez de `Alert.prompt` de propósito: `Alert.prompt` só existe no iOS
  // (é um no-op silencioso no Android) — o resto do app já evita essa API
  // por isso (ver formulário de "novo contador" em SessionScreen.tsx e o
  // formulário de perfil em ProfileScreen.tsx, ambos TextInput inline).
  const [finalizeModalVisible, setFinalizeModalVisible] = useState(false);
  const [practicedPriceText, setPracticedPriceText] = useState('');
  const [savingPracticedPrice, setSavingPracticedPrice] = useState(false);

  const load = useCallback(async () => {
    if (!ready || !db || !user || !id) return;
    setLoading(true);
    try {
      const [loadedProject, loadedLinks, loadedCatalog, loadedPhotos] = await Promise.all([
        getProject(db, id),
        listProjectSupplies(db, id),
        listSupplies(db, user.id),
        listProjectPhotos(db, id),
      ]);

      if (!loadedProject) {
        setNotFound(true);
        return;
      }

      setProject(loadedProject);
      setName(loadedProject.name);
      setNotes(loadedProject.notes ?? '');
      setRecipeUrl(loadedProject.recipeUrl ?? '');
      setLinks(loadedLinks);
      setCatalog(loadedCatalog);
      setPhotos(loadedPhotos);
    } catch (error) {
      console.error('ProjectDetailScreen: falha ao carregar projeto', error);
    } finally {
      setLoading(false);
    }
  }, [ready, db, user, id]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async () => {
    if (!db || !project) return;
    setSaving(true);
    try {
      const updated = await updateProject(db, project.id, {
        name: name.trim() || project.name,
        notes: notes.trim() === '' ? null : notes,
        recipeUrl: recipeUrl.trim() === '' ? null : recipeUrl,
      });
      setProject(updated);
      Alert.alert(t('project.detail.saved'));
    } catch (error) {
      Alert.alert(t('project.detail.saveErrorTitle'), (error as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (status: Project['status']) => {
    if (!db || !project || project.status === status) return;
    try {
      const updated = await updateProject(db, project.id, { status });
      setProject(updated);
      // A finalização em si (mudar o status) já aconteceu e foi salva acima
      // — o diálogo de preço praticado é só um oferecimento à parte, nunca
      // bloqueia a finalização (spec S04: "'pular' não deve bloquear").
      if (status === 'finished') {
        setPracticedPriceText(updated.practicedPrice != null ? String(updated.practicedPrice) : '');
        setFinalizeModalVisible(true);
      }
    } catch (error) {
      Alert.alert(t('project.detail.saveErrorTitle'), (error as Error).message);
    }
  };

  const handleSkipPracticedPrice = () => {
    setFinalizeModalVisible(false);
  };

  const handleSavePracticedPrice = async () => {
    if (!db || !project) return;
    const trimmed = practicedPriceText.trim();
    const practicedPrice = trimmed === '' ? null : parseLocaleNumber(practicedPriceText);
    if (practicedPrice === null && trimmed !== '') {
      Alert.alert(t('project.detail.finalizeDialogErrorTitle'), t('project.detail.finalizeDialogInvalidNumber'));
      return;
    }

    setSavingPracticedPrice(true);
    try {
      const updated = await setProjectPracticedPrice(db, project.id, practicedPrice);
      setProject(updated);
      setFinalizeModalVisible(false);
    } catch (error) {
      Alert.alert(t('project.detail.finalizeDialogErrorTitle'), (error as Error).message);
    } finally {
      setSavingPracticedPrice(false);
    }
  };

  const handleDelete = () => {
    if (!db || !project) return;

    Alert.alert(t('project.detail.deleteConfirmTitle'), t('project.detail.deleteConfirmMessage'), [
      { text: t('project.detail.deleteConfirmCancel'), style: 'cancel' },
      {
        text: t('project.detail.deleteConfirmConfirm'),
        style: 'destructive',
        onPress: async () => {
          try {
            await deleteProject(db, project.id);
            router.back();
          } catch (error) {
            Alert.alert(t('project.detail.deleteErrorTitle'), (error as Error).message);
          }
        },
      },
    ]);
  };

  if (!ready || loading) {
    return (
      <View style={styles.centered}>
        <ActivityIndicator color={colors.sageDeep} />
        <Text style={styles.muted}>{t('project.detail.loading')}</Text>
      </View>
    );
  }

  if (notFound || !project) {
    return (
      <View style={styles.centered}>
        <Text style={styles.muted}>{t('project.detail.notFound')}</Text>
        <Button title={t('project.detail.back')} onPress={() => router.back()} />
      </View>
    );
  }

  return (
    <ScrollView contentContainerStyle={styles.container}>
      <Button title={`< ${t('project.detail.back')}`} variant="ghost" onPress={() => router.back()} />

      <Text style={styles.label}>{t('project.detail.nameLabel')}</Text>
      <TextField value={name} onChangeText={setName} editable={!saving} />

      <Text style={styles.label}>{t('project.detail.statusLabel')}</Text>
      <View style={styles.statusRow}>
        {STATUS_OPTIONS.map((status) => (
          <Chip
            key={status}
            label={t(`project.status.${status}`)}
            active={project.status === status}
            onPress={() => handleStatusChange(status)}
          />
        ))}
      </View>

      <Text style={styles.label}>{t('project.detail.recipeUrlLabel')}</Text>
      <TextField
        placeholder={t('project.detail.recipeUrlPlaceholder')}
        value={recipeUrl}
        onChangeText={setRecipeUrl}
        autoCapitalize="none"
        editable={!saving}
      />

      <Text style={styles.label}>{t('project.detail.notesLabel')}</Text>
      <TextField
        style={styles.notesInput}
        placeholder={t('project.detail.notesPlaceholder')}
        value={notes}
        onChangeText={setNotes}
        multiline
        editable={!saving}
      />

      {saving ? <ActivityIndicator color={colors.sageDeep} /> : <Button title={t('project.detail.save')} onPress={handleSave} />}

      <View style={styles.sessionButtonWrapper}>
        <Button title={t('project.detail.openSessionButton')} onPress={() => router.push(`/sessao/${project.id}`)} />
      </View>

      <View style={styles.pricingButtonWrapper}>
        <Button title={t('project.detail.openPricingButton')} variant="line" onPress={() => router.push(`/preco/${project.id}`)} />
      </View>

      {project.practicedPrice != null && (
        <Text style={styles.practicedPriceSummary}>
          {t('pricing.practicedPriceLabel')}: R$ {formatLocaleNumber(project.practicedPrice)}
        </Text>
      )}

      {db && user && (
        <ProjectSuppliesSection
          db={db}
          userId={user.id}
          projectId={project.id}
          links={links}
          catalog={catalog}
          onLinked={load}
        />
      )}

      {db && user && (
        <ProjectPhotosSection db={db} userId={user.id} projectId={project.id} photos={photos} onPhotoUploaded={load} />
      )}

      <View style={styles.deleteSection}>
        <Button title={t('project.detail.deleteButton')} variant="danger" onPress={handleDelete} />
      </View>

      <Modal visible={finalizeModalVisible} animationType="fade" transparent onRequestClose={handleSkipPracticedPrice}>
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>{t('project.detail.finalizeDialogTitle')}</Text>
            <Text style={styles.modalMessage}>{t('project.detail.finalizeDialogMessage')}</Text>
            <TextField
              placeholder={t('project.detail.finalizeDialogPlaceholder')}
              value={practicedPriceText}
              onChangeText={setPracticedPriceText}
              keyboardType="decimal-pad"
              editable={!savingPracticedPrice}
              autoFocus
            />
            <View style={styles.modalButtonsRow}>
              <Button
                title={t('project.detail.finalizeDialogSkip')}
                variant="ghost"
                onPress={handleSkipPracticedPrice}
                disabled={savingPracticedPrice}
              />
              {savingPracticedPrice ? (
                <ActivityIndicator color={colors.sageDeep} />
              ) : (
                <Button title={t('project.detail.finalizeDialogSave')} onPress={handleSavePracticedPrice} />
              )}
            </View>
          </View>
        </View>
      </Modal>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: { padding: spacing.md, backgroundColor: colors.sand },
  sessionButtonWrapper: { marginTop: spacing.md },
  centered: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: spacing.md, backgroundColor: colors.sand },
  label: { fontFamily: fonts.bodyMedium, fontSize: 13, color: colors.inkSoft, marginTop: spacing.md, marginBottom: spacing.xs },
  notesInput: { minHeight: 80, textAlignVertical: 'top' },
  statusRow: { flexDirection: 'row', flexWrap: 'wrap' },
  muted: { fontFamily: fonts.body, color: colors.thread, marginTop: spacing.sm },
  deleteSection: { marginTop: spacing.xl, marginBottom: spacing.xl + spacing.md },
  pricingButtonWrapper: { marginTop: spacing.sm },
  practicedPriceSummary: { fontFamily: fonts.body, marginTop: spacing.sm, fontSize: 13, color: colors.inkSoft },
  modalOverlay: { flex: 1, backgroundColor: 'rgba(44,47,34,0.4)', justifyContent: 'center', padding: spacing.lg },
  modalContent: { backgroundColor: colors.sand, borderRadius: radii.md, padding: spacing.md },
  modalTitle: { fontFamily: fonts.display, fontSize: 17, color: colors.ink, marginBottom: spacing.sm },
  modalMessage: { fontFamily: fonts.body, fontSize: 14, color: colors.inkSoft, marginBottom: spacing.md },
  modalButtonsRow: { flexDirection: 'row', justifyContent: 'flex-end', gap: spacing.sm, marginTop: spacing.sm },
});
